import { useCallback, useEffect, useRef, useState } from "react";
import {
  ACTIVE_MEDICAL_CASE_CLAUSE_LABEL,
  CARE_RELATIONSHIP_ENDING_REASON_LABEL,
  CARE_RELATIONSHIP_ENDING_REASONS,
  CARE_RELATIONSHIP_NOTE_MAX,
  LEGACY_MEDICAL_CASE_OUTCOME_LABEL,
  type CareRelationshipEndingReason,
  type CareRelationshipEndingRecord,
  type LegacyMedicalCaseOutcomeRecord,
  type MedicalCaseResponse,
} from "@care-by-ella/contracts";
import {
  endCareRelationship,
  fetchMedicalCase,
  revokeCareRelationshipEnding,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useReadOnly } from "./SupportView";

// TES-1200 (Ö-403) — the medical case on the patient overview: whether it is
// active (and Pausat), and its history. The verdict is the server's
// (activeMedicalCase in contracts); nothing here decides it.
//
// TES-1555 S2 (Ö-411, Emelie 2026-10-21) — its one documented ending is an
// ended care relationship, with Emelie's three reasons. A rejection is shown
// as the assessment outcome it is, and the TES-1200 outcomes recorded before
// TES-1555 are listed as history, under the labels they were written with.
//
// `canRecord` is the doctor outside the read-only support view: only she
// documents an ending or revokes it (a new care process). The admin sees the
// panel read-only. Strings not quoted from Emelie are proposed copy.
function when(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function byLine(name: string | null): string {
  return name ? ` · ${name}` : "";
}

export function MedicalCaseOutcomePanel({
  patientId,
  canRecord: canRecordProp,
  onChanged,
  onUnauthorized,
}: {
  patientId: string;
  canRecord: boolean;
  onChanged: () => void;
  onUnauthorized: () => void;
}) {
  // The support view is read-only even if a caller passes canRecord.
  const readOnly = useReadOnly();
  const canRecord = canRecordProp && !readOnly;
  const [data, setData] = useState<MedicalCaseResponse | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<CareRelationshipEndingReason | "">("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  const load = useCallback(async () => {
    try {
      setData(await fetchMedicalCase(patientId));
      setLoadError(false);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorizedRef.current();
        return;
      }
      setLoadError(true);
    }
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handle = async (action: () => Promise<MedicalCaseResponse>) => {
    setBusy(true);
    setError(null);
    try {
      setData(await action());
      setOpen(false);
      setReason("");
      setNote("");
      onChanged();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError("Det gick inte att spara. Ladda om sidan och försök igen.");
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <p className="text-xs text-danger">
        Det gick inte att läsa det medicinska ärendet. Ladda om sidan.
      </p>
    );
  }
  if (!data) {
    return <p className="text-xs text-textSecondary">Hämtar ärendet…</p>;
  }

  const { verdict, endings, legacyOutcomes } = data;
  const standing = endings.find((e) => e.revokedAt === null) ?? null;
  const earlier = endings.filter((e) => e !== standing);
  // There is a care relationship to end: an active case, or a rejected
  // application (an assessment outcome; she may still leave or move).
  const endable =
    standing === null &&
    (verdict.active || verdict.reason === "assessment_rejected");

  return (
    <div className="flex flex-col gap-sm text-sm">
      {verdict.active ? (
        <div className="flex flex-col gap-xs">
          <p className="font-medium">
            {verdict.paused ? "Aktivt · Pausat" : "Aktivt"}
          </p>
          <p className="text-xs text-textSecondary">
            {verdict.clauses
              .map((c) => ACTIVE_MEDICAL_CASE_CLAUSE_LABEL[c])
              .join(", ")}
            .
          </p>
        </div>
      ) : verdict.reason === "care_relationship_ended" && standing ? (
        <div className="flex flex-col gap-xs">
          <p className="font-medium">
            {`Vårdrelationen är avslutad: ${CARE_RELATIONSHIP_ENDING_REASON_LABEL[standing.reason]}`}
          </p>
          <EndingDetails record={standing} />
          {canRecord ? (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void handle(() =>
                  revokeCareRelationshipEnding(patientId, standing.id),
                )
              }
              className="self-start rounded-md border border-surfaceMuted px-md py-xs text-sm text-textPrimary hover:border-brand disabled:opacity-50"
            >
              Öppna ett nytt medicinskt ärende
            </button>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-xs">
          <p className="font-medium">
            {verdict.reason === "assessment_rejected"
              ? "Slutligt avslag"
              : verdict.reason === "account_deleted"
                ? "Kontot är raderat"
                : "Inget aktivt medicinskt ärende"}
          </p>
          <p className="text-xs text-textSecondary">{verdict.message}</p>
        </div>
      )}

      {endable && canRecord ? (
        open ? (
          <div className="flex flex-col gap-sm rounded-md border border-surfaceMuted p-sm">
            <label className="flex flex-col gap-xs">
              <span className="text-xs text-textSecondary">Anledning</span>
              <select
                value={reason}
                disabled={busy}
                onChange={(e) =>
                  setReason(e.target.value as CareRelationshipEndingReason | "")
                }
                className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs"
              >
                <option value="">Välj anledning</option>
                {CARE_RELATIONSHIP_ENDING_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {CARE_RELATIONSHIP_ENDING_REASON_LABEL[r]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-xs">
              <span className="text-xs text-textSecondary">
                Anteckning (valfri)
              </span>
              <textarea
                value={note}
                disabled={busy}
                maxLength={CARE_RELATIONSHIP_NOTE_MAX}
                rows={3}
                onChange={(e) => setNote(e.target.value)}
                className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs"
              />
            </label>
            <p className="text-xs text-textSecondary">
              Avslutet dokumenteras med ditt namn och loggas. Abonnemang och
              behandling ändras inte.
            </p>
            <div className="flex flex-wrap items-center gap-sm">
              <button
                type="button"
                disabled={busy || reason === ""}
                title={reason === "" ? "Välj en anledning." : undefined}
                onClick={() => {
                  if (reason === "") return;
                  const trimmed = note.trim();
                  void handle(() =>
                    endCareRelationship(patientId, {
                      reason,
                      ...(trimmed ? { note: trimmed } : {}),
                    }),
                  );
                }}
                className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
              >
                Avsluta vårdrelationen
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setOpen(false)}
                className="text-sm text-textSecondary underline"
              >
                Avbryt
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="self-start rounded-md border border-surfaceMuted px-md py-xs text-sm text-textPrimary hover:border-brand"
          >
            Avsluta vårdrelationen
          </button>
        )
      ) : null}

      {error ? <p className="text-xs text-danger">{error}</p> : null}

      {earlier.length > 0 ? (
        <div className="flex flex-col gap-xs">
          <h3 className="text-xs font-medium text-textSecondary">
            Tidigare avslutade vårdrelationer
          </h3>
          <ul className="flex flex-col gap-xs">
            {earlier.map((e) => (
              <li key={e.id} className="flex flex-col">
                <span>{CARE_RELATIONSHIP_ENDING_REASON_LABEL[e.reason]}</span>
                <EndingDetails record={e} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {legacyOutcomes.length > 0 ? (
        <div className="flex flex-col gap-xs">
          <h3 className="text-xs font-medium text-textSecondary">
            Utfall registrerade före oktober 2026
          </h3>
          <ul className="flex flex-col gap-xs">
            {legacyOutcomes.map((o) => (
              <li key={o.id} className="flex flex-col">
                <span>{LEGACY_MEDICAL_CASE_OUTCOME_LABEL[o.outcome]}</span>
                <LegacyDetails record={o} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function EndingDetails({ record }: { record: CareRelationshipEndingRecord }) {
  return (
    <div className="flex flex-col text-xs text-textSecondary">
      <span>
        {`Avslutad ${when(record.recordedAt)}${byLine(record.recordedByName)}${
          record.legacyOutcomeId ? " · överförd från ett tidigare utfall" : ""
        }`}
      </span>
      {record.note ? (
        <span className="whitespace-pre-wrap">{`Anteckning: ${record.note}`}</span>
      ) : null}
      {record.revokedAt ? (
        <span>
          {`Nytt ärende öppnat ${when(record.revokedAt)}${byLine(record.revokedByName)}`}
        </span>
      ) : null}
    </div>
  );
}

function LegacyDetails({ record }: { record: LegacyMedicalCaseOutcomeRecord }) {
  return (
    <div className="flex flex-col text-xs text-textSecondary">
      <span>
        {/* TES-1203 — she recorded it herself in the app. */}
        {`Registrerat ${when(record.recordedAt)}${
          record.recordedByPatient
            ? " · av patienten i appen"
            : byLine(record.recordedByName)
        }`}
      </span>
      {record.reason ? (
        <span className="whitespace-pre-wrap">{`Orsak: ${record.reason}`}</span>
      ) : null}
      {record.reopenedAt ? (
        <span>
          {`Nytt ärende öppnat ${when(record.reopenedAt)}${byLine(record.reopenedByName)}`}
        </span>
      ) : null}
    </div>
  );
}
